/**
 * Contact Page - Simple contact form for general inquiries
 *
 * For the full onboarding wizard, see /get-started
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Separator, useFeedback } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { trpc } from '@/lib/trpc'
import { ArrowRight, Mail, Clock } from 'lucide-react'

export default function ContactPage() {
  const breadcrumbs = generateBreadcrumbs('/contact', breadcrumbLabels)

  useSEO({
    title: 'Contact',
    description:
      'Have a question? Get in touch with Fugue Lab. We help music organizations build beautiful websites, manage events, and grow their audience.',
    url: '/contact',
    canonical: '/contact',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Fugue Lab',
      description: "Get in touch with Fugue Lab for questions about your music organization's digital needs.",
      mainEntity: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        email: 'hello@fuguelab.com',
        url: 'https://fuguelab.com',
      },
    },
  })

  const { toast } = useFeedback()
  const [form, setForm] = useState({ name: '', email: '', message: '', honeypot: '' })
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot check
    if (form.honeypot) {
      return
    }

    setIsSubmitting(true)

    try {
      const [result] = await Promise.all([
        trpc.email.send.mutate({
          to: ['garrett@garrettjohnlaw.com'],
          subject: `[Contact Form] New message from ${form.name}`,
          text: `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`,
        }),
        trpc.leads.create
          .mutate({
            name: form.name,
            email: form.email,
            message: form.message,
            source: 'Contact Page',
          })
          .catch(() => {}),
      ])

      if (result.success) {
        setSubmitted(true)
      } else {
        toast('Failed to send message. Please try again.', 'error')
      }
    } catch {
      toast('Failed to send message. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto max-w-2xl px-4 py-24">
        {/* Header */}
        <section className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold tracking-tight">Contact Us</h1>
          <Separator className="bg-border mx-auto mb-4 h-[2px] w-16" />
          <p className="text-muted-foreground text-lg">Have a question? We'd love to hear from you.</p>
        </section>

        {/* Contact Form */}
        {submitted ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-xl font-medium text-green-600">Message sent successfully!</div>
            <p className="text-muted-foreground mb-6">We'll get back to you within 24 hours.</p>
            <Button asChild variant="outline">
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="mb-2 block text-sm font-medium">
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="message" className="mb-2 block text-sm font-medium">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full resize-none rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
                placeholder="How can we help?"
              />
            </div>
            {/* Honeypot field */}
            <input
              type="text"
              name="honeypot"
              value={form.honeypot}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
            />
            <Button type="submit" variant="default" className="w-full py-3" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}

        {/* Contact Info */}
        <div className="border-border mt-16 border-t pt-8">
          <div className="grid gap-6 text-center sm:grid-cols-2 sm:text-left">
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <Mail className="text-muted-foreground h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">Email</p>
                <a
                  href="mailto:hello@fuguelab.com"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  hello@fuguelab.com
                </a>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <Clock className="text-muted-foreground h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">Response Time</p>
                <p className="text-muted-foreground">Within 24 hours</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA to Get Started */}
        <div className="bg-muted/50 mt-16 rounded-xl p-8 text-center">
          <h2 className="mb-2 text-xl font-semibold">Ready to get started?</h2>
          <p className="text-muted-foreground mb-6">
            Tell us about your organization and we'll help you find the right solution.
          </p>
          <Button asChild size="lg">
            <Link to="/get-started">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
